import React, { useMemo, useState, useEffect } from 'react';

/**
 * BirthdatePicker — three native <select> dropdowns for Day / Month / Year.
 *
 * Why not <input type="date">?
 *   Facebook's and Instagram's in-app webviews ship a broken date picker —
 *   tapping the input does nothing on iOS and shows a hidden / unscrollable
 *   year wheel on Android. Real users get stuck (production session showed a
 *   visitor tap the field 17 times in 14 minutes, then gave up). Three plain
 *   selects work everywhere, including in-app browsers, screen readers, and
 *   keyboard-only flows.
 *
 * Props:
 *   value: ISO date string `YYYY-MM-DD` (or empty)
 *   onChange(iso): called whenever a complete date is selected
 *   required, name, testIdPrefix, minAgeYears (default 18)
 */
const MONTHS = [
  { v: 1, label: 'January' }, { v: 2, label: 'February' }, { v: 3, label: 'March' },
  { v: 4, label: 'April' }, { v: 5, label: 'May' }, { v: 6, label: 'June' },
  { v: 7, label: 'July' }, { v: 8, label: 'August' }, { v: 9, label: 'September' },
  { v: 10, label: 'October' }, { v: 11, label: 'November' }, { v: 12, label: 'December' },
];

function daysInMonth(year, month) {
  // month is 1-12. JavaScript Date(0) on day 0 of next month = last day of this month.
  if (!year || !month) return 31;
  return new Date(year, month, 0).getDate();
}

export function BirthdatePicker({ value, onChange, required, minAgeYears = 18, testIdPrefix = 'birthdate' }) {
  // Parse incoming ISO date string into pieces (so the picker stays in sync
  // when the form is reset / pre-filled by saved profile data).
  const initial = useMemo(() => {
    if (!value || typeof value !== 'string') return { d: '', m: '', y: '' };
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return { d: '', m: '', y: '' };
    return { y: m[1], m: String(parseInt(m[2], 10)), d: String(parseInt(m[3], 10)) };
  }, [value]);
  const [day, setDay] = useState(initial.d);
  const [month, setMonth] = useState(initial.m);
  const [year, setYear] = useState(initial.y);

  // Re-sync when parent resets the value externally.
  useEffect(() => { setDay(initial.d); setMonth(initial.m); setYear(initial.y); }, [initial.d, initial.m, initial.y]);

  // Year range: from this year - minAgeYears (oldest legal age) back 100 years.
  const thisYear = new Date().getFullYear();
  const maxYear = thisYear - minAgeYears;
  const minYear = maxYear - 100;
  const years = useMemo(() => {
    const arr = [];
    for (let y = maxYear; y >= minYear; y--) arr.push(y);
    return arr;
  }, [maxYear, minYear]);

  const days = useMemo(() => {
    const max = daysInMonth(parseInt(year, 10), parseInt(month, 10));
    const arr = [];
    for (let d = 1; d <= max; d++) arr.push(d);
    return arr;
  }, [year, month]);

  // Clamp day if month/year change makes it invalid (e.g. Feb 30 → Feb 28).
  useEffect(() => {
    if (day && parseInt(day, 10) > days.length) setDay(String(days.length));
  }, [days.length, day]);

  // Emit ISO string upward when all three pieces are set.
  useEffect(() => {
    if (day && month && year) {
      const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      if (iso !== value) onChange(iso);
    } else if (!day && !month && !year && value) {
      onChange('');
    }
  }, [day, month, year, value, onChange]);

  const baseCls = 'bg-[#000B18] border border-white/20 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF] w-full';

  return (
    <div className="grid grid-cols-3 gap-2" data-testid={testIdPrefix}>
      <select
        value={month} onChange={(e) => setMonth(e.target.value)}
        required={required} className={baseCls} data-testid={`${testIdPrefix}-month`}
      >
        <option value="">Month</option>
        {MONTHS.map(m => <option key={m.v} value={m.v}>{m.label}</option>)}
      </select>
      <select
        value={day} onChange={(e) => setDay(e.target.value)}
        required={required} className={baseCls} data-testid={`${testIdPrefix}-day`}
      >
        <option value="">Day</option>
        {days.map(d => <option key={d} value={d}>{d}</option>)}
      </select>
      <select
        value={year} onChange={(e) => setYear(e.target.value)}
        required={required} className={baseCls} data-testid={`${testIdPrefix}-year`}
      >
        <option value="">Year</option>
        {years.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
}
